import { apiSlice } from "../../../features/api/apiSlice";

function pick(res: any) {
  return res?.data?.data ?? res?.data ?? res;
}
function pickArr(res: any): any[] {
  const d = pick(res);
  return Array.isArray(d) ? d : [];
}

export const examinerApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Examiner: view assigned courses for a diet ──────────────────────────
    examinerGetAssignments: builder.query<any[], string | void>({
      query: (dietId) => `/exam/examiner/assignments${dietId ? `?diet_id=${dietId}` : ""}`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_, __, dietId) => [{ type: "ExaminerAssignments" as const, id: dietId ?? "ALL" }],
    }),

    // ── Examiner: list results for a course + diet ──────────────────────────
    examinerGetResults: builder.query<any[], { courseId: string; dietId: string }>({
      query: ({ courseId, dietId }) =>
        `/exam/examiner/results?course_id=${courseId}&diet_id=${dietId}`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_, __, { courseId, dietId }) => [
        { type: "ExaminerResults" as const, id: `${courseId}:${dietId}` },
      ],
    }),

    // ── Examiner: manual entry (single student score) ──────────────────────
    examinerSaveScore: builder.mutation<any, { courseId: string; dietId: string; studentId: string; score: number }>({
      query: (body) => ({ url: "/exam/examiner/results/manual", method: "POST", body }),
      invalidatesTags: (_, __, { courseId, dietId }) => [
        { type: "ExaminerResults" as const, id: `${courseId}:${dietId}` },
      ],
    }),

    // ── Examiner: update a single score (before submission) ────────────────
    examinerUpdateScore: builder.mutation<any, { resultId: string; score: number }>({
      query: ({ resultId, score }) => ({
        url: `/exam/examiner/results/${resultId}`,
        method: "PUT",
        body: { score },
      }),
      invalidatesTags: [{ type: "ExaminerResults" as const, id: "LIST" }],
    }),

    // ── Examiner: Excel bulk upload ─────────────────────────────────────────
    examinerUploadExcel: builder.mutation<any, FormData>({
      query: (body) => ({ url: "/exam/examiner/results/upload", method: "POST", body, formData: true }),
      invalidatesTags: [{ type: "ExaminerResults" as const, id: "LIST" }],
    }),

    // ── Examiner: submit batch (course + diet) ─────────────────────────────
    examinerSubmitResults: builder.mutation<any, { courseId: string; dietId: string }>({
      query: (body) => ({ url: "/exam/examiner/results/submit", method: "POST", body }),
      invalidatesTags: (_, __, { courseId, dietId }) => [
        { type: "ExaminerResults" as const, id: `${courseId}:${dietId}` },
        { type: "ExaminerAssignments" as const, id: dietId },
      ],
    }),

    // ── Examiner: view feedback comments on a submission ──────────────────
    examinerGetFeedback: builder.query<any[], { courseId: string; dietId: string }>({
      query: ({ courseId, dietId }) =>
        `/exam/examiner/results/feedback?course_id=${courseId}&diet_id=${dietId}`,
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_, __, { courseId, dietId }) => [
        { type: "ExaminerFeedback" as const, id: `${courseId}:${dietId}` },
      ],
    }),

    // ── Examiner: get all applications for a diet (for grading) ───────────
    examinerGetDietApplications: builder.query<any[], { dietId: string; status?: string }>({
      query: ({ dietId, status }) => {
        const q = new URLSearchParams();
        if (status) q.set("status", status);
        const qs = q.toString();
        return `/exam/examiner/diets/${dietId}/applications${qs ? `?${qs}` : ""}`;
      },
      transformResponse: (res: any) => pickArr(res),
      providesTags: (_, __, { dietId }) => [
        { type: "ExaminerResults" as const, id: `diet:${dietId}` },
      ],
    }),

    // ── Examiner: grade an application (creates/updates exam_result) ───────
    examinerGradeDietApplication: builder.mutation<any, { dietId: string; appId: string; body: any }>({
      query: ({ dietId, appId, body }) => ({
        url: `/exam/examiner/diets/${dietId}/applications/${appId}/grade`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { dietId }) => [
        { type: "ExaminerResults" as const, id: `diet:${dietId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useExaminerGetAssignmentsQuery,
  useExaminerGetResultsQuery,
  useExaminerSaveScoreMutation,
  useExaminerUpdateScoreMutation,
  useExaminerUploadExcelMutation,
  useExaminerSubmitResultsMutation,
  useExaminerGetFeedbackQuery,
  useExaminerGetDietApplicationsQuery,
  useExaminerGradeDietApplicationMutation,
} = examinerApi;
